import every from "lodash/every";
import filter from "lodash/filter";
import isNumber from "lodash/isNumber";
import map from "lodash/map";
import some from "lodash/some";
import { useActivityShortlists } from "PFApp/activities/show/hooks/use_activity_shortlists";
import { useActivityTabsStoreActions } from "PFApp/activities/show/store/activity_tabs";
import { ActivityTab } from "PFApp/activities/types/activity_tab";
import { Alert } from "PFComponents/alert";
import { Inline, Stack } from "PFComponents/containers";
import { Select } from "PFComponents/select";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { ClosureReason, ClosureReasonKey } from "PFCore/types";
import { Id } from "PFTypes/id";
import { Option } from "PFTypes/options";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CandidateSelect } from "./candidate_select";
import css from "./vacancy.module.scss";

export type OpenVacancyProps = {
  closureReasons: ClosureReason[];
  reasonValue: string;
  candidateValue: string | number | null;
  selectedProfileIds: Id[];
  onReasonChange: (value: string) => void;
  onCandidateChange: (value: string | number | null) => void;
};

export const OpenVacancy = ({
  closureReasons,
  reasonValue,
  candidateValue,
  selectedProfileIds,
  onReasonChange,
  onCandidateChange
}: OpenVacancyProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.vacancies" });
  const { shortlists } = useActivityShortlists();
  const { changeTab } = useActivityTabsStoreActions();
  const isPermittedTo = useIsCurrentUserPermittedTo();

  const reason = closureReasons.find(({ key }) => key === reasonValue);
  const isAutoComplete = reason && reason.key === ClosureReasonKey.FilledWithNamedResource;
  const showWarning =
    isAutoComplete && isNumber(candidateValue) && !some(shortlists, ["profile.id", candidateValue]);

  const selectOptions: Option<string>[] = useMemo(
    () =>
      map(
        filter(
          closureReasons,
          ({ hidden, restrictedPolicies }) =>
            !hidden && every(restrictedPolicies, (policy) => isPermittedTo(policy))
        ),
        ({ key, label }) => ({ id: key, value: label, original: key })
      ),
    [closureReasons, isPermittedTo]
  );

  return (
    <Stack>
      <Inline>
        <Select<string>
          className={css.columnItem}
          label={t("selectClosureReason")}
          value={reasonValue}
          options={selectOptions}
          onChange={(option: Option<string>) => onReasonChange(option.original)}
        />
        {reason &&
          (isAutoComplete ? (
            <CandidateSelect
              candidateId={candidateValue as number}
              selectedProfileIds={selectedProfileIds}
              onCandidateChange={onCandidateChange}
            />
          ) : (
            <InputFieldSet
              className={css.columnItem}
              label={t("details")}
              onChange={onCandidateChange}
              value={candidateValue || ""}
              maxLength={255}
            />
          ))}
      </Inline>

      {showWarning && (
        <Alert
          action={{
            onClick: () => changeTab(ActivityTab.Shortlist),
            text: t("pickSomeoneFromShortlist")
          }}
          title={t("candidateNotOptimalMatch")}
        />
      )}
    </Stack>
  );
};
